import { useMutation, UseMutationOptions } from '@tanstack/react-query';
import axios from 'axios';
import { useCallback } from 'react';
import { generatePath } from 'react-router-dom';
import { Button, useModal } from 'yoso';
import { SmsVerificationPopup } from '~/components/Adventure/Popup/SmsVerificationPopup/SmsVerificationPopup';
import { useLogin } from '~/components/Auth';
import { useEmbeddedViewer } from '~/components/Embedded/Embedded.hooks';
import { BaseError } from '~/components/Error/BaseError';
import { useDefaultErrorHandler } from '~/components/Error/Error.hooks';
import { HttpError } from '~/components/Error/HttpError';
import { t } from '~/components/I18n/i18n';
import { T } from '~/components/I18n/Translation';
import { BlueWallet } from '~/components/Icon/BlueWallet';
import exclamation from '~/components/Icon/exclamation2.png';
import { LandType, ZoneType } from '~/components/Land/Land.hooks';
import { ParticipationBottomSheet } from '~/components/Land/Participation/Bottomsheet/ParticipationBottomSheet';
import { Alert } from '~/components/Modal/Alert/Alert';
import { useBottomSheet } from '~/components/Modal/BottomSheet';
import { Confirm, styles } from '~/components/Modal/Confirm/Confirm';
import { UseDosiminMutationOptions } from '~/components/Query/Query.hooks';
import { useZone, VaultVerifyError } from '../Zone/LandZone.hooks';

export type ParticipateLandParams = {
  landId: number;
  zoneId: number;
};

export type ParticipateLand = {
  success: boolean;
  landId: number;
  landType: LandType;
  zoneId: number;
  zoneType: ZoneType;
  zoneNumber: number;
};

export type LandParticipateError = HttpError<{
  responseData: {
    walletSmsUri?: string;
  };
}> &
  VaultVerifyError;

export const participateLandUrl = '/api/citizen/v1/lands/:landId/zones/:zoneId/participation';

export const participateLand = async (params: ParticipateLandParams) => {
  const url = generatePath(participateLandUrl, params);
  const { data } = await axios.post<ParticipateLand>(url);
  return {
    ...data,
    zoneNumber: Number(data.zoneType.split('_')[1]),
  };
};

export const useParticipateLandErrorHandler = () => {
  const { open } = useModal();
  const defaultErrorHandler = useDefaultErrorHandler();
  const vaultErrorHandler = useVaultErrorHandler();
  return useCallback(
    async (error: LandParticipateError) => {
      const { walletSmsUri, sellerInfoUri } = error.payload?.response?.data?.responseData || {};
      switch (error.message) {
        case 'SMS_VERIFICATION_FAILED':
          open(
            <SmsVerificationPopup
              description={t('citizen_sms_verification_popup_land_desc')}
              walletSmsUri={walletSmsUri!}
            />
          );
          break;
        case 'VAULT_VERIFICATION_FAILED': {
          await vaultErrorHandler(sellerInfoUri!);
          break;
        }
        case 'PARTICIPATE_LAND_MIN_CONDITION_UNMET':
          await open(
            <Alert
              icon={<img src={exclamation} width={100} height={100} alt="participation failed" />}
              title={<T name="citizen_land_join_error_popup_title" />}
              description={<T name="citizen_land_join_error_popup_desc" />}
              confirmButton={({ confirm }) => (
                <Button
                  className={styles.confirmButton}
                  color="primary"
                  data-event="click"
                  data-event-target="error_goto_land_btn"
                  onClick={confirm}>
                  <T name="citizen_button_title_go_land" />
                </Button>
              )}
              // data-event="impression"
              // data-event-category="land_join_error_popup"
              // data-event-impression-id="{category}"
            />
          );
          break;
        default:
          defaultErrorHandler();
          break;
      }
    },
    [defaultErrorHandler, vaultErrorHandler, open]
  );
};

export const useParticipateLand = (
  options?: UseDosiminMutationOptions<ParticipateLand, LandParticipateError, ParticipateLandParams>
) => {
  const onError = useParticipateLandErrorHandler();
  return useMutation<ParticipateLand, LandParticipateError, ParticipateLandParams>(
    (params) => participateLand(params),
    {
      onError,
      ...options,
    }
  );
};

export const useParticipateErrorHandler = () => {
  const login = useLogin();
  const handleDefaultError = useDefaultErrorHandler();
  const vaultErrorHandler = useVaultErrorHandler();
  return useCallback(
    async (error: VaultVerifyError) => {
      const { sellerInfoUri } = error.payload?.response?.data?.responseData || {};
      switch (error.message) {
        case 'UNAUTHORIZED':
          login();
          break;
        case 'VAULT_VERIFICATION_FAILED':
          await vaultErrorHandler(sellerInfoUri!);
          break;
        default:
          handleDefaultError();
      }
    },
    [handleDefaultError, login, vaultErrorHandler]
  );
};

export const useParticipationBottomSheet = (
  params: ParticipateLandParams,
  options?: UseMutationOptions<void, LandParticipateError>
) => {
  const handleError = useParticipateErrorHandler();
  const { refetch } = useZone(params, {
    enabled: false,
    useErrorBoundary: false,
    onError: handleError,
  });
  const bottomSheet = useBottomSheet();

  return useMutation<void, LandParticipateError>({
    mutationFn: async () => {
      await refetch({ throwOnError: true });
      await bottomSheet.open(<ParticipationBottomSheet {...params} />).then(({ confirmed }) => {
        if (!confirmed) throw new BaseError('DISMISSED');
      });
    },
    onError: () => {},
    ...options,
  });
};

export const useVaultErrorHandler = () => {
  const modal = useModal();
  const wallet = useEmbeddedViewer();
  return useCallback(
    async (sellerInfoUri: string) => {
      const { confirmed } = await modal.open(
        <Confirm
          icon={<BlueWallet />}
          title={t('citizen_land_detail_guide_banner2_title')}
          description={t('citizen_land_detail_guide_banner2_desc1')}
          confirmText={t('citizen_button_title_goto_register')}
          dismissText={t('citizen_button_title_next_time')}
        />
      );
      if (confirmed && sellerInfoUri) {
        // NOTE: Force open wallet in other micro task
        requestAnimationFrame(() => wallet.open(sellerInfoUri));
      }
    },
    [modal, wallet]
  );
};
